import {
	RebasedWebSocketMessageAbstract,
	RebasedWebSocketMessageDirection,
	RebasedWebSocketMessageType,
} from '@trovata/app/core/models/rebased-web-socket.model';
import { InlineChartData } from '@trovata/app/shared/models/inline-chart.model';

// ==========================================
// Constants
// ==========================================

const SCHEMA_BASE: string = 'https://data.trovata.io/api/websocket/schemas/client-server';

/**
 * Longest `runId` the backend accepts. A longer value fails request validation outright and the run
 * never starts, so the minter below must stay well inside it.
 */
const RUN_ID_MAX_LENGTH: number = 64;

// ==========================================
// Custom Error Classes
// ==========================================

/** Typed error for WebSocket connection failures. Uses instanceof for type-safe error differentiation (ES2022+). */
export class WebSocketConnectionError extends Error {
	override readonly name: 'WebSocketConnectionError' = 'WebSocketConnectionError' as const;
	constructor(message: string = 'WebSocket is not connected. Please check your connection and try again.') {
		super(message);
	}
}

// ==========================================
// Payload Interfaces
// ==========================================

export interface AgentChatConfigurationPayload {
	readonly q: string;
	readonly sessionId?: string;
	readonly Authorization?: string;
	/** Correlation id for this one run — see `mintAgentRunId`. */
	readonly runId?: string;
}

export interface AgentConfigurationResponsePayload {
	readonly status: 'processing' | 'success' | 'error';
	readonly message?: string;
	readonly sessionId?: string;
	/** Inline charts keyed by `{{CHART:<token>}}` token; terminal success frames only. */
	readonly inlineCharts?: Readonly<Record<string, InlineChartData>> | null;
	/** Echo of the requesting run's `runId`. Null/absent from a backend that predates the echo. */
	readonly runId?: string | null;
}

export interface AgentConfigurationTextDeltaPayload {
	readonly sessionId: string;
	readonly delta: string;
	/** Echo of the requesting run's `runId`. Null/absent from a backend that predates the echo. */
	readonly runId?: string | null;
}

export interface AgentChatPreviewPayload {
	readonly q: string;
	readonly Authorization?: string;
	/** IANA timezone the preview run should anchor dates to (an unsaved agent has no schedule rule to read it from). */
	readonly timezone?: string;
	/** Correlation id for this one run — see `mintAgentRunId`. */
	readonly runId?: string;
}

export interface AgentConfigurationPreviewResponsePayload {
	readonly status: 'processing' | 'success' | 'error';
	/** Populated on processing frames with a short label for the tool currently running (replaced per tool); carries the failure reason on error frames. */
	readonly message?: string;
	/** Inline charts keyed by `{{CHART:<token>}}` token; terminal success frames only. */
	readonly inlineCharts?: Readonly<Record<string, InlineChartData>> | null;
	/** Echo of the requesting run's `runId`. Null/absent from a backend that predates the echo. */
	readonly runId?: string | null;
}

export interface AgentConfigurationPreviewTextDeltaPayload {
	readonly delta: string;
	/** Echo of the requesting run's `runId`. Null/absent from a backend that predates the echo. */
	readonly runId?: string | null;
}

// ==========================================
// Message Interfaces
// ==========================================

export interface AgentChatConfigurationMessage extends RebasedWebSocketMessageAbstract {
	readonly schema: string;
	readonly type: RebasedWebSocketMessageType.aiAgentChatConfiguration;
	readonly direction: RebasedWebSocketMessageDirection.clientToServer;
	readonly payload: AgentChatConfigurationPayload;
}

export interface AgentConfigurationResponseMessage extends RebasedWebSocketMessageAbstract {
	readonly schema: string;
	readonly type: RebasedWebSocketMessageType.aiAgentConfigurationResponse;
	readonly direction: RebasedWebSocketMessageDirection.serverToClient;
	readonly payload: AgentConfigurationResponsePayload;
}

export interface AgentConfigurationTextDeltaMessage extends RebasedWebSocketMessageAbstract {
	readonly schema: string;
	readonly type: RebasedWebSocketMessageType.aiAgentConfigurationTextDelta;
	readonly direction: RebasedWebSocketMessageDirection.serverToClient;
	readonly payload: AgentConfigurationTextDeltaPayload;
}

export interface AgentChatPreviewMessage extends RebasedWebSocketMessageAbstract {
	readonly schema: string;
	readonly type: RebasedWebSocketMessageType.aiAgentChatPreview;
	readonly direction: RebasedWebSocketMessageDirection.clientToServer;
	readonly payload: AgentChatPreviewPayload;
}

export interface AgentConfigurationPreviewResponseMessage extends RebasedWebSocketMessageAbstract {
	readonly schema: string;
	readonly type: RebasedWebSocketMessageType.aiAgentConfigurationPreviewResponse;
	readonly direction: RebasedWebSocketMessageDirection.serverToClient;
	readonly payload: AgentConfigurationPreviewResponsePayload;
}

export interface AgentConfigurationPreviewTextDeltaMessage extends RebasedWebSocketMessageAbstract {
	readonly schema: string;
	readonly type: RebasedWebSocketMessageType.aiAgentConfigurationPreviewTextDelta;
	readonly direction: RebasedWebSocketMessageDirection.serverToClient;
	readonly payload: AgentConfigurationPreviewTextDeltaPayload;
}

// ==========================================
// Stream Event Type
// ==========================================

export type AgentStreamEvent =
	| { readonly type: 'processing'; readonly message?: string }
	| { readonly type: 'delta'; readonly content: string }
	| {
			readonly type: 'complete';
			readonly inlineCharts?: Readonly<Record<string, InlineChartData>> | null;
			/** Degrade warning from the terminal frame — e.g. charts omitted because the payload was oversized. */
			readonly message?: string;
	  }
	| { readonly type: 'error'; readonly message: string };

// ==========================================
// Builder Functions
// ==========================================

/**
 * Mints the correlation id for one agent run.
 *
 * `crypto.randomUUID()` is only defined in a secure context and is missing from some runtimes, so a
 * random fallback keeps a run attributable instead of throwing where it is absent. Uniqueness only
 * has to hold between the runs of a single tab — the backend routes frames per socket connection —
 * and both forms stay far inside the length the backend accepts.
 */
export function mintAgentRunId(): string {
	const runId: string = globalThis.crypto?.randomUUID?.() ?? `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
	return runId.slice(0, RUN_ID_MAX_LENGTH);
}

export function buildAgentChatConfigurationRequest(prompt: string, sessionId?: string, authorization?: string, runId?: string): AgentChatConfigurationMessage {
	const request: AgentChatConfigurationMessage = {
		schema: `${SCHEMA_BASE}/${RebasedWebSocketMessageType.aiAgentChatConfiguration}.v1.0.json`,
		type: RebasedWebSocketMessageType.aiAgentChatConfiguration,
		direction: RebasedWebSocketMessageDirection.clientToServer,
		payload: {
			q: prompt,
			...(sessionId ? { sessionId } : {}),
			...(authorization ? { Authorization: authorization } : {}),
			...(runId ? { runId } : {}),
		},
	};
	return request;
}

export function buildAgentChatPreviewRequest(prompt: string, authorization?: string, timezone?: string, runId?: string): AgentChatPreviewMessage {
	const request: AgentChatPreviewMessage = {
		schema: `${SCHEMA_BASE}/${RebasedWebSocketMessageType.aiAgentChatPreview}.v1.0.json`,
		type: RebasedWebSocketMessageType.aiAgentChatPreview,
		direction: RebasedWebSocketMessageDirection.clientToServer,
		payload: {
			q: prompt,
			...(authorization ? { Authorization: authorization } : {}),
			...(timezone ? { timezone } : {}),
			...(runId ? { runId } : {}),
		},
	};
	return request;
}
